import { useEffect, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X } from "lucide-react";

const emptyForm = { name: "", sku: "", brand_id: "", category_id: "", description: "", price: "", image_url: "", specs: [{ k: "", v: "" }], is_active: true };

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [q, setQ] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);

    const load = async () => {
        const params = q ? { q } : {};
        const { data } = await api.get("/admin/products", { params });
        setProducts(data);
    };

    useEffect(() => {
        (async () => {
            const [b, c] = await Promise.all([api.get("/admin/brands"), api.get("/admin/categories")]);
            setBrands(b.data);
            setCategories(c.data);
            load();
        })();
        // eslint-disable-next-line
    }, []);

    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...emptyForm, brand_id: brands[0]?.id || "", category_id: categories[0]?.id || "" });
        setShowForm(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            name: p.name || "",
            sku: p.sku || "",
            brand_id: p.brand_id || "",
            category_id: p.category_id || "",
            description: p.description || "",
            price: p.price ?? "",
            image_url: p.image_url || "",
            specs: Object.keys(p.specs || {}).length ? Object.entries(p.specs).map(([k, v]) => ({ k, v: String(v) })) : [{ k: "", v: "" }],
            is_active: p.is_active !== false,
        });
        setShowForm(true);
    };

    const doDelete = async (p) => {
        if (!confirm(`Excluir "${p.name}"?`)) return;
        try {
            await api.delete(`/admin/products/${p.id}`);
            toast.success("Produto excluído");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const uploadImage = async (file) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, image_url: data.url }));
            toast.success("Imagem carregada");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha no upload");
        } finally { setUploading(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const specs = {};
            form.specs.forEach(({ k, v }) => { if (k.trim()) specs[k.trim()] = v; });
            const body = {
                name: form.name,
                sku: form.sku || null,
                brand_id: form.brand_id,
                category_id: form.category_id || null,
                description: form.description || null,
                price: form.price === "" ? null : Number(form.price),
                image_url: form.image_url || null,
                specs,
                is_active: !!form.is_active,
            };
            if (editing) await api.put(`/admin/products/${editing.id}`, body);
            else await api.post("/admin/products", body);
            toast.success(editing ? "Produto atualizado" : "Produto criado");
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao salvar");
        } finally { setSaving(false); }
    };

    return (
        <div className="p-8">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-neutral-500">Núcleo de dados</div>
                    <h1 className="mt-1 font-heading font-black text-3xl tracking-tight">Produtos</h1>
                </div>
                <div className="flex gap-2">
                    <input data-testid="products-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="h-11 px-4 bg-white border border-neutral-300 focus:outline-none focus:border-brand-blue" />
                    <button data-testid="new-product-btn" onClick={openCreate} className="h-11 px-4 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-blue-hover flex items-center gap-2">
                        <Plus size={16} /> Novo produto
                    </button>
                </div>
            </div>

            <div className="mt-6 bg-white border border-neutral-300 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-neutral-50 font-mono text-xs uppercase tracking-widest text-neutral-500">
                        <tr>
                            <th className="text-left p-3">Imagem</th>
                            <th className="text-left p-3">Nome</th>
                            <th className="text-left p-3">Marca</th>
                            <th className="text-left p-3">Categoria</th>
                            <th className="text-left p-3">SKU</th>
                            <th className="text-left p-3">Ativo</th>
                            <th className="text-right p-3">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.length === 0 ? (
                            <tr><td colSpan={7} className="p-12 text-center font-mono text-sm text-neutral-500">nenhum produto cadastrado</td></tr>
                        ) : products.map((p, i) => (
                            <tr key={p.id} className={`border-t border-neutral-200 ${i % 2 ? "bg-neutral-50/50" : ""}`} data-testid={`product-row-${p.id}`}>
                                <td className="p-3">
                                    {p.image_url ? <img src={fileUrl(p.image_url)} alt="" className="w-12 h-12 object-cover border border-neutral-200" /> :
                                        <div className="w-12 h-12 bg-neutral-100 border border-neutral-200 grid place-items-center text-[10px] font-mono text-neutral-400">n/a</div>}
                                </td>
                                <td className="p-3 font-medium">{p.name}</td>
                                <td className="p-3 font-mono text-xs">{p.brand?.name || "—"}</td>
                                <td className="p-3 text-neutral-600">{p.category?.name || "—"}</td>
                                <td className="p-3 font-mono text-xs text-neutral-500">{p.sku || "—"}</td>
                                <td className="p-3">
                                    <span className={`inline-block px-2 py-1 text-[10px] font-mono uppercase tracking-widest ${p.is_active ? "bg-green-100 text-green-800" : "bg-neutral-100 text-neutral-500"}`}>{p.is_active ? "ativo" : "inativo"}</span>
                                </td>
                                <td className="p-3">
                                    <div className="flex justify-end gap-1">
                                        <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 hover:bg-neutral-200 text-neutral-700"><Pencil size={14} /></button>
                                        <button data-testid={`delete-${p.id}`} onClick={() => doDelete(p)} className="p-2 hover:bg-red-100 text-red-700"><Trash2 size={14} /></button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white border border-neutral-300 max-h-[90vh] overflow-y-auto" data-testid="product-form">
                        <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between">
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">{editing ? "editar" : "novo"}</div>
                                <div className="font-heading font-bold text-xl">{editing ? editing.name : "Novo produto"}</div>
                            </div>
                            <button type="button" onClick={() => setShowForm(false)} className="p-2 hover:bg-neutral-100"><X size={18} /></button>
                        </div>

                        <div className="p-6 space-y-5">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Nome">
                                    <input required data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
                                </Field>
                                <Field label="SKU / Código">
                                    <input data-testid="product-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="input" />
                                </Field>
                                <Field label="Marca">
                                    <select required data-testid="product-brand" value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })} className="input">
                                        <option value="">Selecionar…</option>
                                        {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </Field>
                                <Field label="Categoria">
                                    <select data-testid="product-category" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="input">
                                        <option value="">— sem categoria —</option>
                                        {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </Field>
                                <Field label="Preço (opcional, R$)">
                                    <input type="number" step="0.01" data-testid="product-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input" />
                                </Field>
                                <Field label="Status">
                                    <label className="flex items-center gap-2 h-11">
                                        <input type="checkbox" data-testid="product-active" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                                        <span className="text-sm">Ativo no catálogo</span>
                                    </label>
                                </Field>
                            </div>

                            <Field label="Descrição">
                                <textarea data-testid="product-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" />
                            </Field>

                            <Field label="Imagem do produto">
                                <div className="flex items-center gap-4">
                                    {form.image_url && <img src={fileUrl(form.image_url)} alt="" className="w-20 h-20 object-cover border border-neutral-200" />}
                                    <label className="cursor-pointer inline-flex items-center gap-2 h-11 px-4 bg-neutral-100 hover:bg-neutral-200 border border-neutral-300 text-sm font-medium">
                                        <Upload size={14} />
                                        {uploading ? "enviando…" : (form.image_url ? "Trocar imagem" : "Selecionar imagem")}
                                        <input data-testid="product-image-input" type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])} />
                                    </label>
                                    {form.image_url && <button type="button" onClick={() => setForm({ ...form, image_url: "" })} className="text-sm text-red-600 underline">remover</button>}
                                </div>
                            </Field>

                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Especificações técnicas (chave → valor)</div>
                                <div className="space-y-2">
                                    {form.specs.map((s, idx) => (
                                        <div key={idx} className="grid grid-cols-[1fr,1fr,auto] gap-2">
                                            <input placeholder="ex: Potência" value={s.k} onChange={(e) => { const c = [...form.specs]; c[idx].k = e.target.value; setForm({ ...form, specs: c }); }} className="input" />
                                            <input placeholder="ex: 5 CV" value={s.v} onChange={(e) => { const c = [...form.specs]; c[idx].v = e.target.value; setForm({ ...form, specs: c }); }} className="input" />
                                            <button type="button" onClick={() => setForm({ ...form, specs: form.specs.filter((_, i) => i !== idx) })} className="h-11 w-11 grid place-items-center hover:bg-red-50 text-red-600 border border-neutral-300"><X size={14} /></button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" data-testid="add-spec" onClick={() => setForm({ ...form, specs: [...form.specs, { k: "", v: "" }] })} className="mt-2 text-sm text-brand-blue font-medium">+ adicionar spec</button>
                            </div>
                        </div>

                        <div className="sticky bottom-0 bg-white border-t border-neutral-200 p-4 flex justify-end gap-2">
                            <button type="button" onClick={() => setShowForm(false)} className="h-11 px-5 bg-neutral-100 hover:bg-neutral-200 text-sm font-medium">Cancelar</button>
                            <button data-testid="save-product" disabled={saving} className="h-11 px-6 bg-brand-blue text-white font-heading font-bold uppercase tracking-wider text-sm hover:bg-brand-blue-hover disabled:opacity-60">
                                {saving ? "salvando…" : "Salvar"}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <style>{`.input{width:100%;height:2.75rem;padding:0 1rem;background:#fff;border:1px solid #cbd5e1;font-size:.875rem}.input:focus{outline:none;border-color:#0A4D96;box-shadow:0 0 0 2px rgba(10,77,150,.15)}`}</style>
        </div>
    );
}

const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-xs font-mono uppercase tracking-widest text-neutral-500">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);
